import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Dropdown, Header, Segment, Message } from "semantic-ui-react";
import { pdApiActions, PdApiActions } from "../../actions/pdApiActions";
import { hasRole } from "../../utils/utils";
import KanbanBoard from "./KanbanBoard";

type StateProps = {
  licenseUploadStatus: State.LicenseUploadState;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
};

type LicenseUploadPortalProps = StateProps & DispatchProps;

const DEVICE_TYPE_OPTIONS = [
  { key: "lift", value: "lift", text: "Lift" },
  { key: "escalator", value: "escalator", text: "Escalator" },
  { key: "door", value: "door", text: "Door" },
];

const USER_GROUP_OPTIONS = [
  { key: "kone", value: "kone", text: "KONE" },
  { key: "non-kone", value: "non-kone", text: "Non-KONE" },
];

class LicenseUploadPortal extends React.Component<LicenseUploadPortalProps> {
  componentDidUpdate(prevProps: LicenseUploadPortalProps): void {
    const { licenseUploadStatus } = this.props;
    const prev = prevProps.licenseUploadStatus;

    // Auto-fetch records when device type or user group changes
    if (
      licenseUploadStatus.selectedDeviceType &&
      licenseUploadStatus.selectedUserGroup &&
      (licenseUploadStatus.selectedDeviceType !== prev.selectedDeviceType ||
        licenseUploadStatus.selectedUserGroup !== prev.selectedUserGroup)
    ) {
      this.fetchAllRecords();
    }
  }

  private fetchAllRecords = (): void => {
    const { selectedDeviceType, selectedUserGroup } = this.props.licenseUploadStatus;
    if (selectedDeviceType && selectedUserGroup) {
      this.props.pdApiActions.fetchLicenseRecord(selectedDeviceType, selectedUserGroup, "qa");
      this.props.pdApiActions.fetchLicenseRecord(selectedDeviceType, selectedUserGroup, "prod");
      this.props.pdApiActions.fetchLicenseRecord(selectedDeviceType, selectedUserGroup, "lota");
    }
  };

  private handleDeviceTypeChange = (_e: React.SyntheticEvent, data: any): void => {
    this.props.pdApiActions.setLicenseDeviceType(data.value);
  };

  private handleUserGroupChange = (_e: React.SyntheticEvent, data: any): void => {
    this.props.pdApiActions.setLicenseUserGroup(data.value);
  };

  private handleUpload = (jsonContent: string): void => {
    const { selectedDeviceType, selectedUserGroup } = this.props.licenseUploadStatus;
    this.props.pdApiActions.uploadTlgQa(selectedDeviceType, selectedUserGroup, jsonContent);
  };

  private handlePushToProd = (): void => {
    const { selectedDeviceType, selectedUserGroup } = this.props.licenseUploadStatus;
    this.props.pdApiActions.pushToProd(selectedDeviceType, selectedUserGroup);
  };

  private handleRequestLota = (comment: string): void => {
    const { selectedDeviceType, selectedUserGroup } = this.props.licenseUploadStatus;
    this.props.pdApiActions.requestLota(selectedDeviceType, selectedUserGroup, comment);
  };

  private handleApprove = (): void => {
    const { selectedDeviceType, selectedUserGroup } = this.props.licenseUploadStatus;
    this.props.pdApiActions.approveLota(selectedDeviceType, selectedUserGroup);
  };

  private handleReject = (reason: string): void => {
    const { selectedDeviceType, selectedUserGroup } = this.props.licenseUploadStatus;
    this.props.pdApiActions.rejectLota(selectedDeviceType, selectedUserGroup, reason);
  };

  render(): JSX.Element {
    const { activeUser, licenseUploadStatus } = this.props;

    if (!activeUser || !hasRole(activeUser, "license-upload")) {
      return (
        <Message warning>
          <Message.Header>Access Denied</Message.Header>
          <p>You do not have the required role to access the License Upload portal.</p>
        </Message>
      );
    }

    const isApprover = hasRole(activeUser, "license-approval");
    const filtersSelected =
      licenseUploadStatus.selectedDeviceType && licenseUploadStatus.selectedUserGroup;

    return (
      <div style={{ padding: "1em" }}>
        <Header as="h2">KCL JSON Upload Portal</Header>

        <Segment>
          <Dropdown
            placeholder="Select Device Type"
            selection
            options={DEVICE_TYPE_OPTIONS}
            value={licenseUploadStatus.selectedDeviceType}
            onChange={this.handleDeviceTypeChange}
            style={{ marginRight: "1em" }}
          />
          <Dropdown
            placeholder="Select User Group"
            selection
            options={USER_GROUP_OPTIONS}
            value={licenseUploadStatus.selectedUserGroup}
            onChange={this.handleUserGroupChange}
          />
        </Segment>

        {filtersSelected ? (
          <KanbanBoard
            licenseUploadStatus={licenseUploadStatus}
            isApprover={isApprover}
            onUpload={this.handleUpload}
            onPushToProd={this.handlePushToProd}
            onRequestLota={this.handleRequestLota}
            onApprove={this.handleApprove}
            onReject={this.handleReject}
          />
        ) : (
          <Message info>
            <p>Please select a device type and user group to begin.</p>
          </Message>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: State.Root): StateProps => ({
  licenseUploadStatus: state.licenseUploadStatus,
  activeUser: state.main.activeUser,
});

const mapDispatchToProps = (dispatch: Dispatch<PdApiActions>): DispatchProps => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(LicenseUploadPortal);
